/*
 * ruthik-faq.js — drives the FAQ / policy accordion sections.
 *
 * The search box and category pills live in ruthik-faq-hero while the questions
 * live in ruthik-faq-list, so nothing here is scoped to a single section root:
 * it queries the document and wires whatever is present.
 *
 * Because BOTH sections reference this file, the browser executes it twice on
 * the FAQ page. Everything below is therefore idempotent — elements are marked
 * once wired and skipped on any later pass. Without that guard each question
 * ended up with two click listeners, so a click opened and immediately
 * re-closed it and nothing appeared to happen. The same guard covers
 * shopify:section:load, which re-runs init after a theme-editor edit.
 *
 * State lives at module scope rather than inside init so a second pass shares
 * one set of items instead of building a rival closure over the same DOM.
 */
(function () {
  'use strict';

  var items = [];
  var pills = [];
  var searchInput = null;
  var clearBtn = null;
  var emptyState = null;
  var resetBtn = null;
  var countEl = null;

  var activeCategory = '';
  var query = '';

  /* ---- accordion ---------------------------------------------------- */

  function closeItem(item) {
    var panel = item.querySelector('[data-faq-panel]');
    var trigger = item.querySelector('[data-faq-trigger]');
    if (!panel || !trigger) return;
    panel.style.height = panel.scrollHeight + 'px';
    void panel.offsetHeight; // reflow, so the transition starts from a real height
    panel.style.height = '0px';
    if(item.closest('.rfl-band--creator')){panel.inert=true;panel.setAttribute('aria-hidden','true');}
    item.setAttribute('data-faq-open', 'false');
    trigger.setAttribute('aria-expanded', 'false');
  }

  function openItem(item) {
    var panel = item.querySelector('[data-faq-panel]');
    var trigger = item.querySelector('[data-faq-trigger]');
    if (!panel || !trigger) return;
    if(item.closest('.rfl-band--creator')){panel.inert=false;panel.removeAttribute('aria-hidden');}
    item.setAttribute('data-faq-open', 'true');
    trigger.setAttribute('aria-expanded', 'true');
    panel.style.height = panel.scrollHeight + 'px';
  }

  function toggleItem(item) {
    if (item.getAttribute('data-faq-open') === 'true') {
      closeItem(item);
      return;
    }
    // one open at a time, matching the design
    items.forEach(function (other) {
      if (other !== item && other.getAttribute('data-faq-open') === 'true') {
        closeItem(other);
      }
    });
    openItem(item);
  }

  /* ---- filtering ---------------------------------------------------- */

  function apply() {
    var q = query.trim().toLowerCase();
    var visible = 0;

    items.forEach(function (item) {
      var matchesCategory =
        !activeCategory || item.getAttribute('data-faq-category') === activeCategory;
      var matchesQuery =
        !q || (item.getAttribute('data-faq-text') || '').indexOf(q) !== -1;

      var show = matchesCategory && matchesQuery;
      item.hidden = !show;
      if (show) {
        visible++;
      } else if (item.getAttribute('data-faq-open') === 'true') {
        closeItem(item);
      }
    });

    if (emptyState) emptyState.hidden = visible !== 0;
    if (countEl) countEl.textContent = visible === 1 ? '1 question' : visible + ' questions';
    if (clearBtn) clearBtn.hidden = !q;
  }

  /* ---- wiring (idempotent) ------------------------------------------ */

  function wireItem(item) {
    if (item.getAttribute('data-faq-wired') === 'true') return;
    var trigger = item.querySelector('[data-faq-trigger]');
    var panel = item.querySelector('[data-faq-panel]');
    if (!trigger || !panel) return;
    item.setAttribute('data-faq-wired', 'true');

    if (item.getAttribute('data-faq-open') === 'true') {
      panel.style.height = panel.scrollHeight + 'px';
      trigger.setAttribute('aria-expanded', 'true');
    } else {
      panel.style.height = '0px';
      trigger.setAttribute('aria-expanded', 'false');
    }

    trigger.addEventListener('click', function () {
      toggleItem(item);
    });

    // once open, let the panel grow with its content instead of staying pinned
    // to the height we measured
    panel.addEventListener('transitionend', function (e) {
      if (e.propertyName !== 'height') return;
      if (item.getAttribute('data-faq-open') === 'true') panel.style.height = 'auto';
    });
  }

  function wirePill(pill) {
    if (pill.getAttribute('data-faq-wired') === 'true') return;
    pill.setAttribute('data-faq-wired', 'true');
    pill.addEventListener('click', function () {
      var cat = pill.getAttribute('data-faq-pill') || '';
      // clicking the active pill clears the filter and shows everything
      activeCategory = activeCategory === cat ? '' : cat;
      pills.forEach(function (p) {
        var on = p.getAttribute('data-faq-pill') === activeCategory;
        p.setAttribute('data-faq-pill-active', on ? 'true' : 'false');
        p.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      apply();
    });
  }

  function init() {
    items = Array.prototype.slice.call(document.querySelectorAll('[data-faq-item]'));
    if (!items.length) return;

    pills = Array.prototype.slice.call(document.querySelectorAll('[data-faq-pill]'));
    searchInput = document.querySelector('[data-faq-search]');
    clearBtn = document.querySelector('[data-faq-search-clear]');
    emptyState = document.querySelector('[data-faq-empty]');
    resetBtn = document.querySelector('[data-faq-reset]');
    countEl = document.querySelector('[data-faq-count]');

    items.forEach(wireItem);
    pills.forEach(wirePill);

    if (searchInput && searchInput.getAttribute('data-faq-wired') !== 'true') {
      searchInput.setAttribute('data-faq-wired', 'true');
      searchInput.addEventListener('input', function () {
        query = searchInput.value || '';
        apply();
      });
      searchInput.addEventListener('keydown', function (e) {
        // don't let Enter submit a surrounding form
        if (e.key === 'Enter') e.preventDefault();
        if (e.key === 'Escape') {
          searchInput.value = '';
          query = '';
          apply();
        }
      });
    }

    if (clearBtn && clearBtn.getAttribute('data-faq-wired') !== 'true') {
      clearBtn.setAttribute('data-faq-wired', 'true');
      clearBtn.hidden = true;
      clearBtn.addEventListener('click', function () {
        if (!searchInput) return;
        searchInput.value = '';
        query = '';
        searchInput.focus();
        apply();
      });
    }

    if (resetBtn && resetBtn.getAttribute('data-faq-wired') !== 'true') {
      resetBtn.setAttribute('data-faq-wired', 'true');
      resetBtn.addEventListener('click', function () {
        activeCategory = '';
        query = '';
        if (searchInput) searchInput.value = '';
        pills.forEach(function (p) {
          p.setAttribute('data-faq-pill-active', 'false');
          p.setAttribute('aria-pressed', 'false');
        });
        apply();
      });
    }

    apply();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // the theme editor re-renders a section without reloading the page
  document.addEventListener('shopify:section:load', init);
})();
